import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import { NOTIFICATION_TYPE } from '../../resources/enums';
import { ReactComponent as DashboardCommunitiesIcon } from '../../resources/icons/DashboardCommunitiesIcon.svg';
import { INotification } from '../../resources/interfaces';
import { ActivityIcon, Award, Banknote, Calculator, CreditCard, FileText, Shield, Target, Users, X } from 'lucide-react';
import { GrAction } from 'react-icons/gr';

interface NotificationsProps {
  allNotifications: INotification[];
  meNotifications: INotification[];
  handleOnclick: (notification: INotification) => void;
  /** Dismiss a single notification (mark read + remove) without navigating. */
  onDismiss?: (notification: INotification) => void;
}

const Notifications: React.FC<NotificationsProps> = ({
  allNotifications,
  meNotifications,
  handleOnclick,
  onDismiss,
}) => {
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>([]);
  const [showAll] = useState<boolean>(false);

  const handleCheckboxClick = (id: string) => {
    if (selectedNotifications.includes(id)) {
      setSelectedNotifications(
        selectedNotifications.filter((notificationId) => notificationId !== id)
      );
    } else {
      setSelectedNotifications([...selectedNotifications, id]);
    }
  };

  const notificationsToShow = showAll ? allNotifications : meNotifications;

  // The full feed is already in memory (the navbar polls it for the bell
  // badge), so paginate client-side: reveal a growing slice as the user
  // scrolls rather than re-fetching. `IntersectionObserver` watches a
  // sentinel scoped to the scroll container and reveals the next batch.
  const PAGE_SIZE = 15;
  const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Only reset the revealed window when switching between the all/me lists.
  // Deliberately NOT keyed on the list length: the 30s poll and dismissing
  // a single item both change the length, and resetting there would snap the
  // user back to the first batch mid-scroll (looked like a page refresh).
  // `visibleCount` is naturally clamped by the slice + revealMore below.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [showAll]);

  const hasMore = visibleCount < notificationsToShow.length;
  const revealMore = useCallback(() => {
    setVisibleCount((c) => Math.min(c + PAGE_SIZE, notificationsToShow.length));
  }, [notificationsToShow.length]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => entries[0]?.isIntersecting && revealMore(),
      { root: scrollRef.current, rootMargin: '80px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, revealMore]);

  const visibleNotifications = notificationsToShow.slice(0, visibleCount);

  return (
    <div className="w-full py-5 h-full flex flex-col min-h-0">
      {notificationsToShow.length > 0 && (
        <div className="flex-1 flex flex-col min-h-0">
          <div ref={scrollRef} className="px-5 overflow-y-auto flex-1 min-h-0">
            {/* AnimatePresence drives the exit animation when an item is
                removed from the list (dismiss/read). The exiting row collapses
                its own height, so the rows below slide up to fill the gap.
                NOTE: no `layout` prop — combined with the `x` transform it
                makes framer apply scale corrections that visually squish the
                row content horizontally mid-animation. */}
            <AnimatePresence initial={false}>
              {visibleNotifications.map((notification: INotification) => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, height: 0, x: 24 }}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                  className="overflow-hidden">
                  <Notification
                    notification={notification}
                    onCheckboxClick={handleCheckboxClick}
                    isChecked={selectedNotifications.includes(notification.id)}
                    onClick={() => {
                      handleOnclick(notification);
                    }}
                    onDismiss={onDismiss ? () => onDismiss(notification) : undefined}
                    viewed={notification.viewed}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
            {hasMore && (
              <div ref={sentinelRef} className="h-8 flex items-center justify-center">
                <span className="text-2xs text-gray-400">Loading more…</span>
              </div>
            )}
          </div>
        </div>
      )}

      {showAll && allNotifications?.length == 0 && (
        <div className="h-20 flex items-center justify-center font-semibold text-lg">
          <span>No notifications</span>
        </div>
      )}

      {!showAll && meNotifications?.length == 0 && (
        <div className="h-20 flex items-center justify-center font-semibold text-lg">
          <span>No notifications</span>
        </div>
      )}
    </div>
  );
};

interface NotificationProps {
  notification: INotification;
  onCheckboxClick?: (id: string) => void;
  onClick?: () => void;
  onDismiss?: () => void;
  isChecked: boolean;
  showCheckBox?: boolean;
  viewed: string;
}

const Notification: React.FC<NotificationProps> = ({
  notification,
  onCheckboxClick,
  onClick,
  onDismiss,
  isChecked,
  viewed,
  showCheckBox = false
}) => {
  const handleCheckboxClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onCheckboxClick?.(notification.id);
  };

  const getNotificationIconFromType = (type: NOTIFICATION_TYPE) => {
    switch (type) {
      case NOTIFICATION_TYPE.LEAD:
        return <Target />;
      case NOTIFICATION_TYPE.CLAIM:
        return <Shield />;
      case NOTIFICATION_TYPE.COMMISSION:
        return <Award />;
      case NOTIFICATION_TYPE.COMMUNITY:
        return <DashboardCommunitiesIcon />;
      case NOTIFICATION_TYPE.CUSTOMER:
        return <Users />;
      case NOTIFICATION_TYPE.PAYMENT:
        return <CreditCard />;
      case NOTIFICATION_TYPE.PAYOUT:
        return <Banknote />;
      case NOTIFICATION_TYPE.POLICY:
        return <FileText />;
      case NOTIFICATION_TYPE.QUOTE:
        return <Calculator />;
      case NOTIFICATION_TYPE.STAFF:
        return <Users />;
      case NOTIFICATION_TYPE.TEAM:
        return <Users />;
      case NOTIFICATION_TYPE.TASK:
        return <GrAction />;
      default:
        return null;
    }
  };

  return (
    <div className={`flex items-center border-b py-2 gap-5 cursor-pointer`}>
      {showCheckBox && (
        <input
          type="checkbox"
          checked={isChecked}
          onChange={handleCheckboxClick}
          className="cursor-pointer"
        />
      )}
      <div className="size-8 rounded-full bg-primary-surface flex items-center justify-center p-2 shrink-0">
        {getNotificationIconFromType(notification?.type)}
      </div>
      <div className="min-w-0 flex-1">
        <p
          onClick={onClick}
          className={`text-sm ${viewed === 'true' ? 'text-gray-500' : 'text-black  hover:font-semibold '
            }`}>
          {notification.message}
        </p>
        <p className="text-2xs">{moment(notification.date).format('DD-MM-YY, h:mm:ss a')}</p>
      </div>
      {onDismiss && (
        <button
          type="button"
          aria-label="Dismiss notification"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="ml-auto shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none">
          <X size={16} />
        </button>
      )}
    </div>
  );
};

export default Notifications;
